import React, { useState, useEffect, useRef } from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MLoader from "@/components/ui/atoms/MLoader";
import styles from "../document.module.css";
import { MButton } from "@/components/ui/atoms/MButtons";
import * as TablerIcons from "@tabler/icons-react";
import MInputField from "@/components/ui/atoms/MInputField";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { useDrawerKeyboardFit } from "@/hooks/useDrawerKeyboardFit";

interface NoteDrawerProps {
  open: boolean;
  onClose: () => void;
  onDone: (fields: {
    type: "personal" | "family";
    title: string;
    content: string;
  }) => void;
  loading?: boolean;
  noteId?: string;
  initialData?: { type: "personal" | "family"; title: string; content: string };
  mode?: "add" | "edit";
  onDelete?: () => void;
  disableTypeToggle?: boolean;
  noteType?: "personal" | "family"; // NEW: if set, always use this type and hide the type toggle
}

const NoteDrawer: React.FC<NoteDrawerProps> = ({
  open,
  onClose,
  onDone,
  loading,
  noteId,
  initialData,
  mode = "add",
  onDelete,
  disableTypeToggle = false,
  noteType,
}) => {
  // Disable type toggle if editing
  const typeToggleDisabled = disableTypeToggle || mode === "edit" || !!noteType;
  const [noteTypeState, setNoteTypeState] = useState<"personal" | "family">(
    noteType || initialData?.type || "personal"
  );
  const [title, setTitle] = useState(initialData?.title || "");
  const [content, setContent] = useState(initialData?.content || "");
  const [submitting, setSubmitting] = useState(false);
  // Tracks whether the on-screen keyboard is up so we can hide the Done
  // button — same UX pattern as the other write-mode screens. Detected via
  // visualViewport.height shrinking below window.innerHeight.
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  // Ref on the textarea so we can keep it in view as the user types — the
  // FooterDrawer's focusin handler only runs once on focus, not on every
  // keystroke.
  const contentTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Sit the drawer just above the keyboard (shared FooterDrawer left as-is).
  // NoteDrawer keeps its own isKeyboardOpen below for hiding the Done button.
  useDrawerKeyboardFit(open, "note-drawer");

  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      // ~150px threshold to avoid noise from URL bar collapse / safe area.
      setIsKeyboardOpen(window.innerHeight - vv.height > 150);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      setIsKeyboardOpen(false);
    };
  }, [open]);

  // Prefill on open or when initialData changes (edit mode)
  useEffect(() => {
    if (mode === "edit" && open && initialData) {
      setNoteTypeState(noteType || initialData.type);
      setTitle(initialData.title);
      setContent(initialData.content);
    } else if (!open) {
      setNoteTypeState(noteType || "personal");
      setTitle("");
      setContent("");
    }
  }, [open, mode, initialData]);

  const isValid = !!title.trim() && !!content.trim();

  const handleDone = async () => {
    setSubmitting(true);
    onDone({
      type: noteType || noteTypeState,
      title: title.trim(),
      content: content.trim(),
    });
    setSubmitting(false);
  };

  return (
    <>
      <FooterDrawer
        open={open}
        onClose={onClose}
        title={mode === "edit" ? "Edit Note" : "Add Note"}
        allowKeyboard={true}
        className="note-drawer"
      >
        {loading ? (
          <MLoader />
        ) : (
          <div className={styles.noteDrawerContainer}>
            {/* Hide type toggle if forceType is set */}
            {!noteType && (
              <>
                <div className={styles.noteDrawerTypeLabel}>Note Type</div>
                <div className={styles.noteDrawerTypeToggles}>
                  <MButton
                    variant={
                      noteTypeState === "personal" ? "filled" : "outline"
                    }
                    size="md"
                    style={{ width: "48%" }}
                    onClick={() => setNoteTypeState("personal")}
                    disabled={typeToggleDisabled}
                    leftIcon={<TablerIcons.IconUser size={18} />}
                    className={
                      noteTypeState === "personal" && typeToggleDisabled
                        ? styles.alwaysBlueWhenSelected
                        : ""
                    }
                  >
                    My Note
                  </MButton>
                  <MButton
                    variant={noteTypeState === "family" ? "filled" : "outline"}
                    size="md"
                    style={{ width: "48%" }}
                    onClick={() => setNoteTypeState("family")}
                    disabled={typeToggleDisabled}
                    leftIcon={<TablerIcons.IconUsers size={18} />}
                    className={
                      noteTypeState === "family" && typeToggleDisabled
                        ? styles.alwaysBlueWhenSelected
                        : ""
                    }
                  >
                    Family Note
                  </MButton>
                </div>
              </>
            )}
            <MInputField
              label="What is this note about?"
              showLabel
              placeholder="Note Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="md"
              variant="Outlined"
              maxLength={30}
              isDrawerInput
              labelClassName={styles.noteDrawerLabel}
            />
            <MTextArea
              label="Add Details"
              placeholder="What would you like to share with your family?"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                // Keep the cursor end of the textarea in view as the user
                // types — without this the line being typed slips behind
                // the keyboard once content extends past the visible area.
                const el = contentTextareaRef.current;
                if (el) {
                  requestAnimationFrame(() => {
                    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
                  });
                }
              }}
              rows={8}
              maxLength={500}
              variant="drawerInput"
              labelClassName={styles.noteDrawerLabel}
              ref={contentTextareaRef}
            />
            {/* Hide the Done button while the keyboard is open to free up
                vertical space for the textarea. It reappears the moment the
                keyboard dismisses — same pattern as the rest of the app. */}
            {!isKeyboardOpen && (
              <div className={styles.noteDrawerDoneButtonRow}>
                <MButton
                  size="lg"
                  variant="filled"
                  className={styles.noteDrawerDoneButton}
                  disabled={!isValid || submitting || loading}
                  onClick={handleDone}
                >
                  Done
                </MButton>
              </div>
            )}
          </div>
        )}
      </FooterDrawer>
    </>
  );
};

export default NoteDrawer;
